import React, { useState, useEffect } from 'react';
import { Button } from '../ui/button';
import { ArrowRight, Play, Sparkles, TrendingUp, Zap, ExternalLink, X } from 'lucide-react';
import { Link } from 'react-router-dom';

const HeroSection = () => {
  const [currentWord, setCurrentWord] = useState(0);
  const [showDemoPopup, setShowDemoPopup] = useState(false);
  const words = ['comprendre', 'simuler', 'créer', 'optimiser'];

  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentWord((prev) => (prev + 1) % words.length);
    }, 3000);
    return () => clearInterval(interval);
  }, [words.length]);

  return (
    <section className="relative min-h-screen flex items-center justify-center overflow-hidden pt-24 md:pt-20">
      {/* Background avec gradient animé */}
      <div className="absolute inset-0 bg-gradient-to-br from-slate-50 via-white to-blue-50">
        <div className="absolute inset-0 opacity-30" style={{
          backgroundImage: `url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%23f1f5f9' fill-opacity='0.4'%3E%3Cpath d='m36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")`
        }}></div>
      </div>

      {/* Éléments flottants décoratifs - Bleu royal */}
      <div className="absolute top-1/4 left-1/4 w-72 h-72 bg-gradient-to-r from-blue-400 to-blue-800 rounded-full mix-blend-multiply filter blur-xl opacity-15 animate-float"></div>
      <div className="absolute top-3/4 right-1/4 w-96 h-96 bg-gradient-to-r from-blue-300 to-blue-700 rounded-full mix-blend-multiply filter blur-xl opacity-15 animate-float" style={{animationDelay: '2s'}}></div>
      <div className="absolute bottom-1/4 left-1/2 w-80 h-80 bg-gradient-to-r from-blue-500 to-blue-900 rounded-full mix-blend-multiply filter blur-xl opacity-15 animate-float" style={{animationDelay: '4s'}}></div>

      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
        {/* Titre principal avec animation */}
        <h1 className="text-4xl md:text-7xl lg:text-8xl font-display font-black mb-8 animate-fadeInUp break-words">
          <span className="text-gray-900">Le blog pour</span>
          <br />
          <span className="relative inline-block">
            <span 
              key={currentWord}
              className="gradient-text animate-fadeIn"
            >
              {words[currentWord]}
            </span>
          </span>
          <br />
          <span className="text-gray-900">votre entreprise</span>
        </h1>

        {/* Sous-titre */}
        <p className="text-xl md:text-2xl text-gray-600 mb-12 max-w-4xl mx-auto leading-relaxed animate-fadeInUp" style={{animationDelay: '0.2s'}}>
          Guides experts, simulateurs gratuits et conseils pratiques pour créer et développer votre entreprise comme un pro
        </p>

        {/* CTAs modernes */}
        <div className="flex flex-col sm:flex-row gap-4 justify-center mb-16 animate-fadeInUp" style={{animationDelay: '0.4s'}}>
          <Link to="/simulateurs">
            <Button 
              size="lg" 
              className="btn-modern gradient-primary text-white px-8 py-4 text-lg font-semibold rounded-2xl hover:shadow-2xl hover:scale-105 transition-all duration-300 group"
            >
              <Zap className="h-5 w-5 mr-2 group-hover:rotate-12 transition-transform" />
              Simuler gratuitement
              <ArrowRight className="h-5 w-5 ml-2 group-hover:translate-x-1 transition-transform" />
            </Button>
          </Link>
          
          <Button 
            size="lg" 
            variant="ghost"
            className="btn-modern glass border border-white/30 backdrop-blur-md px-8 py-4 text-lg font-semibold rounded-2xl hover:bg-white/20 group"
            onClick={() => setShowDemoPopup(true)}
            data-testid="demo-button"
          >
            <Play className="h-5 w-5 mr-2" />
            Voir la démo
          </Button>
        </div>

        {/* Stats modernes */}
        <div className="grid grid-cols-3 gap-4 sm:gap-8 max-w-4xl mx-auto">
          <div className="text-center">
            <div className="text-2xl sm:text-3xl md:text-4xl font-bold text-gray-900 mb-1">65+</div>
            <div className="text-gray-600 text-xs sm:text-base">Guides disponibles</div>
          </div>
          <div className="text-center">
            <div className="text-2xl sm:text-3xl md:text-4xl font-bold text-gray-900 mb-1">15+</div>
            <div className="text-gray-600 text-xs sm:text-base">Simulateurs gratuits</div>
          </div>
          <div className="text-center">
            <div className="text-2xl sm:text-3xl md:text-4xl font-bold text-gray-900 mb-1">2026</div>
            <div className="text-gray-600 text-xs sm:text-base">Données à jour</div>
          </div>
        </div>

      </div>

      {/* Popup Demo */}
      {showDemoPopup && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm"
          onClick={() => setShowDemoPopup(false)}
          data-testid="demo-popup-overlay"
        >
          <div
            className="bg-white rounded-2xl shadow-2xl max-w-md w-full mx-4 p-8 relative animate-fadeInUp"
            onClick={(e) => e.stopPropagation()}
            data-testid="demo-popup"
          >
            <button
              onClick={() => setShowDemoPopup(false)}
              className="absolute top-4 right-4 text-gray-400 hover:text-gray-600 transition-colors"
              data-testid="demo-popup-close"
            >
              <X className="h-5 w-5" />
            </button>

            <div className="text-center">
              <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-5">
                <Play className="h-8 w-8 text-blue-600" />
              </div>

              <h3 className="text-xl font-bold text-gray-900 mb-3">
                Démo bientôt disponible
              </h3>

              <p className="text-gray-600 mb-6 leading-relaxed">
                La démo de l'application Bozzam n'est pas encore disponible. En attendant, découvrez notre plateforme tout-en-un pour freelances.
              </p>

              <div className="flex flex-col gap-3">
                <Button
                  className="w-full bg-blue-600 hover:bg-blue-700 text-white py-3 rounded-xl font-semibold group"
                  onClick={() => window.open(process.env.REACT_APP_BOZZAM_APP_URL, '_blank')}
                  data-testid="demo-popup-cta"
                >
                  Découvrir Bozzam
                  <ExternalLink className="h-4 w-4 ml-2 group-hover:translate-x-0.5 group-hover:-translate-y-0.5 transition-transform" />
                </Button>

                <Button
                  variant="ghost"
                  className="w-full text-gray-500 hover:text-gray-700"
                  onClick={() => setShowDemoPopup(false)}
                >
                  Fermer
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}
    </section>
  );
};

export default HeroSection;
